<template>
  <div class="stepper-editor-wrap">
    <main class="page">

      <p class="breadcrumb">
        SEQ / Admin /
        <router-link to="/admin/questions" class="breadcrumb-link">Questions</router-link>
        / {{ breadcrumbTail }}
      </p>

      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">{{ pageTitle }}</h1>
          <p class="page-subtitle">Numeric stepper: user picks an integer with + and −</p>
        </div>
        <div class="page-header-actions">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancel"
          >Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!isValid || saving"
            @click="onSave"
          >{{ saving ? 'Saving…' : (isEdit ? 'Save changes' : 'Create question') }}</button>
        </div>
      </div>

      <p
        v-if="serverError"
        class="server-error"
        role="alert"
      >{{ serverError }}</p>

      <div
        v-if="isEdit && answerCount > 0"
        class="answer-warning"
        role="status"
      >
        <span class="warning-icon" aria-hidden="true">⚠️</span>
        This question has {{ answerCount }}
        {{ answerCount === 1 ? 'answer' : 'answers' }}.
        Editing labels or values may affect analysis.
      </div>

      <div v-if="loadError" class="load-error">
        <p>{{ loadError }}</p>
        <router-link to="/admin/questions" class="back-link">← Back</router-link>
      </div>

      <div v-else-if="loading" class="loading-state">
        Loading question…
      </div>

      <div v-else class="editor-grid">
        <!-- ───────── LEFT: form ───────── -->
        <section class="form-column">

          <!-- Common fields -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Identification</legend>

            <div class="field">
              <label class="field-label" :for="`${uid}-code`">Code</label>
              <input
                :id="`${uid}-code`"
                v-model="form.code"
                type="text"
                class="field-input mono"
                :class="{ 'has-error': errors.code && touched.code }"
                placeholder="VEG_PORTIONS_DAY"
                autocomplete="off"
                spellcheck="false"
                @blur="touched.code = true"
                @input="onCodeInput"
              >
              <p class="field-hint">
                Short identifier, uppercase letters, digits, and underscores.
                Must be unique within your organization.
              </p>
              <p
                v-if="errors.code && touched.code"
                class="field-error"
              >{{ errors.code }}</p>
              <p
                v-if="isEdit && form.code !== initialSnapshot.code"
                class="field-hint dim"
              >Changing the code may affect analytics scripts that reference it.</p>
            </div>

            <div class="field">
              <label class="field-label">Section</label>
              <button
                type="button"
                class="section-pick-btn"
                :class="{ 'is-unassigned': form.section_id == null }"
                @click="showSectionPicker = true"
              >
                <span class="section-pick-label">{{ sectionPickLabel }}</span>
                <span class="section-pick-action">{{ form.section_id == null ? 'Pick section' : 'Change' }}</span>
              </button>
              <button
                v-if="form.section_id != null"
                type="button"
                class="section-clear-btn"
                @click="form.section_id = null"
              >Clear (Unassigned)</button>
            </div>

            <div class="field">
              <label class="field-label">Status</label>
              <div class="radio-row">
                <label class="radio-option">
                  <input
                    type="radio"
                    v-model="form.status"
                    value="active"
                  >
                  <span>Active</span>
                </label>
                <label class="radio-option">
                  <input
                    type="radio"
                    v-model="form.status"
                    value="archived"
                  >
                  <span>Archived</span>
                </label>
              </div>
            </div>
          </fieldset>

          <!-- Stepper-specific fields. Translatable fields sit under a tab
               strip; Range is language-neutral and stays below it. -->
          <fieldset class="fieldset">
            <legend class="fieldset-legend">Question content</legend>

            <LocaleSelector
              v-model="activeLang"
              :locales="SUPPORTED_LOCALES"
              :labels="LOCALE_LABELS"
              :filled-fn="isLocaleFilled"
              hint="Fill in any or all languages"
              aria-label="Translation language"
            />

            <div class="field">
              <label class="field-label" :for="`${uid}-title-${activeLang}`">
                Question title <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
              </label>
              <textarea
                :id="`${uid}-title-${activeLang}`"
                v-model="form.content.title[activeLang]"
                class="field-input field-textarea"
                :class="{ 'has-error': errors.title && touched.title }"
                rows="2"
                :placeholder="titlePlaceholder"
                @blur="touched.title = true"
              ></textarea>
              <p class="field-hint">
                The main question text. Required in at least one language.
              </p>
              <p
                v-if="errors.title && touched.title"
                class="field-error"
              >{{ errors.title }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-prompt-${activeLang}`">
                Helper prompt <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <input
                :id="`${uid}-prompt-${activeLang}`"
                v-model="form.content.prompt[activeLang]"
                type="text"
                class="field-input"
                :placeholder="promptPlaceholder"
              >
              <p class="field-hint">
                Optional text shown below the stepper. Useful for instructions.
              </p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-unit-${activeLang}`">
                Unit label <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}, plural form</span>
              </label>
              <input
                :id="`${uid}-unit-${activeLang}`"
                v-model="form.content.unit_label[activeLang]"
                type="text"
                class="field-input"
                :class="{ 'has-error': errors.unit_label && touched.unit_label }"
                :placeholder="unitPlaceholder"
                @blur="touched.unit_label = true"
              >
              <p class="field-hint">
                Shown next to the number, e.g. "3 Portionen". Use the plural
                form so it reads acceptably for any number. Required in at
                least one language.
              </p>
              <p
                v-if="errors.unit_label && touched.unit_label"
                class="field-error"
              >{{ errors.unit_label }}</p>
            </div>

            <div class="field">
              <label class="field-label" :for="`${uid}-help-${activeLang}`">
                Help text <span class="lang-tag">{{ LOCALE_LABELS[activeLang] }}</span>
                <span class="optional">(optional)</span>
              </label>
              <textarea
                :id="`${uid}-help-${activeLang}`"
                v-model="form.help_text[activeLang]"
                class="field-input field-textarea"
                rows="3"
                :placeholder="helpPlaceholder"
              ></textarea>
              <p class="field-hint">
                Optional explanation shown when the user taps an info button.
                Leave empty in all languages to hide the info button.
              </p>
            </div>

            <!-- Range is language-neutral: the same min/default/max applies
                 regardless of the active translation tab. -->
            <div class="field range-field">
              <label class="field-label">Range</label>
              <div class="range-row">
                <div class="range-cell">
                  <label class="range-sublabel" :for="`${uid}-min`">Minimum</label>
                  <input
                    :id="`${uid}-min`"
                    v-model.number="form.content.min"
                    type="number"
                    step="1"
                    class="field-input number-input"
                    :class="{ 'has-error': errors.range }"
                  >
                </div>
                <div class="range-cell">
                  <label class="range-sublabel" :for="`${uid}-default`">Default</label>
                  <input
                    :id="`${uid}-default`"
                    v-model.number="form.content.default"
                    type="number"
                    step="1"
                    class="field-input number-input"
                    :class="{ 'has-error': errors.range }"
                  >
                </div>
                <div class="range-cell">
                  <label class="range-sublabel" :for="`${uid}-max`">Maximum</label>
                  <input
                    :id="`${uid}-max`"
                    v-model.number="form.content.max"
                    type="number"
                    step="1"
                    class="field-input number-input"
                    :class="{ 'has-error': errors.range }"
                  >
                </div>
              </div>
              <p class="field-hint">
                Same min, default, and maximum apply to all languages.
              </p>
              <p
                v-if="errors.range"
                class="field-error"
              >{{ errors.range }}</p>
            </div>
          </fieldset>

        </section>

        <!-- ───────── RIGHT: live preview ───────── -->
        <aside class="preview-column">
          <div class="preview-label">Live preview</div>
          <div class="preview-frame">
            <NumericStepperRenderer
              :title="form.content.title"
              :prompt="form.content.prompt"
              :unit-label="form.content.unit_label"
              :locale="activeLang"
              :min="safePreviewMin"
              :max="safePreviewMax"
              :model-value="safePreviewDefault"
              :section-label="previewSectionLabel"
              :question-index="PREVIEW_INDEX"
              :total-questions="PREVIEW_TOTAL"
              :can-go-back="true"
              :is-last="false"
              :primer-label="primerLabel"
              :primer-content="primerContent"
              :preview="true"
            />
          </div>
          <p class="preview-note">
            Mock screen for the
            <strong>{{ LOCALE_LABELS[activeLang] }}</strong>
            translation. Switch tabs in the form to preview another
            language. The progress chrome is illustrative — actual
            position depends on the questionnaire.
          </p>
        </aside>
      </div>

    </main>

    <!-- Discard-changes confirm dialog -->
    <div
      v-if="showLeaveConfirm"
      class="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="leave-title"
      @click.self="onCancelLeave"
    >
      <div class="modal modal-confirm">
        <h2 id="leave-title" class="modal-title">Discard changes?</h2>
        <p class="modal-text">Your unsaved changes will be lost.</p>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="onCancelLeave"
          >Keep editing</button>
          <button
            type="button"
            class="btn btn-danger"
            @click="onConfirmLeave"
          >Discard</button>
        </div>
      </div>
    </div>

    <!-- Section picker popup -->
    <SectionPickerPopup
      v-if="showSectionPicker"
      :current-section-id="form.section_id"
      @select-section="onSectionPicked"
      @close="showSectionPicker = false"
    />

    <!-- Toast -->
    <transition name="toast-fade">
      <div
        v-if="toast"
        class="toast"
        :class="`toast-${toast.kind}`"
        role="status"
        aria-live="polite"
      >{{ toast.text }}</div>
    </transition>
  </div>
</template>

<script>
import NumericStepperRenderer from '@/components/quiz/NumericStepperRenderer.vue';
import LocaleSelector from '@/components/admin/LocaleSelector.vue';
import SectionPickerPopup from '@/components/admin/SectionPickerPopup.vue';
import apiService from '@/services/apiService';
import {
  questionsState,
  blankStepperContent,
  blankI18n,
  ensureAllLocales,
  localizeI18n,
  CODE_REGEX,
  SUPPORTED_LOCALES,
  DEFAULT_LOCALE,
  LOCALE_LABELS,
} from '../_data/questions.js';

let uidCounter = 0;

// Fake "Frage 3 von 7" context for the editor preview chrome. The real
// position is determined by the questionnaire at quiz time; this just
// gives the author a sense of what the screen looks like in context.
const PREVIEW_INDEX = 2;
const PREVIEW_TOTAL = 7;

// Trim + drop empty entries from an i18n object. Used by serializeForm
// for both dirty-check snapshots and the create/update payload — empty
// locale strings become absent keys, so an untouched FR tab doesn't
// pollute the saved record with `{fr: ''}`.
function trimI18n(i18n) {
  const out = {};
  if (i18n && typeof i18n === 'object') {
    for (const loc of SUPPORTED_LOCALES) {
      const v = i18n[loc];
      if (typeof v === 'string' && v.trim()) {
        out[loc] = v.trim();
      }
    }
  }
  return out;
}

// True if any locale in the i18n object holds non-empty text. Used by
// the title/unit_label validation: each needs at least one language.
function hasAnyLocaleFilled(i18n) {
  if (!i18n || typeof i18n !== 'object') return false;
  return SUPPORTED_LOCALES.some(loc => {
    const v = i18n[loc];
    return typeof v === 'string' && v.trim().length > 0;
  });
}

export default {
  name: 'StepperEditor',
  components: { NumericStepperRenderer, LocaleSelector, SectionPickerPopup },
  data() {
    uidCounter += 1;
    return {
      uid: `se-${uidCounter}`,
      PREVIEW_INDEX,
      PREVIEW_TOTAL,
      SUPPORTED_LOCALES,
      LOCALE_LABELS,
      activeLang: DEFAULT_LOCALE,
      form: this.buildBlankForm(),
      initialSnapshot: null,
      touched: {
        code: false,
        title: false,
        unit_label: false,
      },
      serverError: null,
      loadError: null,
      loading: false,
      saving: false,
      answerCount: 0,
      // Codes the backend has rejected as already-in-use (409). Keeps the
      // inline error visible until the user changes the code, without
      // requiring a full client-side list of existing codes.
      rejectedCodes: [],
      // Leave-confirm flow: parks the next() callback while the dialog is up.
      showLeaveConfirm: false,
      showSectionPicker: false,
      pendingNext: null,
      // Cancel button bypasses Vue Router (no navigation), so we use this
      // flag to differentiate "user clicked Cancel" from "browser back" —
      // the modal asks once, then we navigate manually.
      cancelPending: false,
      toast: null,
      toastTimer: null,
    };
  },
  computed: {
    isEdit() {
      return this.$route.name === 'QuestionEditorEdit';
    },
    questionId() {
      return this.isEdit ? Number(this.$route.params.id) : null;
    },
    sections() {
      return questionsState.sections;
    },
    sectionPickLabel() {
      if (this.form.section_id == null) return 'Unassigned';
      const s = this.sections.find(x => x.id === this.form.section_id);
      if (!s) return `Section ${this.form.section_id}`;
      return this.sectionOptionLabel(s);
    },
    breadcrumbTail() {
      if (this.isEdit) {
        return this.initialSnapshot
          ? `Edit · ${this.initialSnapshot.code || 'question'}`
          : 'Edit';
      }
      return 'New stepper question';
    },
    pageTitle() {
      return this.isEdit ? 'Edit question' : 'New stepper question';
    },
    errors() {
      const e = {};
      const code = (this.form.code || '').trim();
      if (!code) {
        e.code = 'Code is required.';
      } else if (!CODE_REGEX.test(code)) {
        e.code = 'Code must be uppercase letters, digits or underscores. Max 32 chars.';
      } else if (this.rejectedCodes.includes(code)) {
        // Backend told us this collides — keep the error until the user changes it.
        e.code = 'This code is already used by another question.';
      }

      // Title and unit_label each need at least one non-empty locale.
      // Authors can fill in any combination of EN/DE/FR/IT.
      if (!hasAnyLocaleFilled(this.form.content.title)) {
        e.title = 'Question title is required in at least one language.';
      }
      if (!hasAnyLocaleFilled(this.form.content.unit_label)) {
        e.unit_label = 'Unit label is required in at least one language.';
      }

      const { min, max, default: def } = this.form.content;
      if (!Number.isInteger(min) || !Number.isInteger(max) || !Number.isInteger(def)) {
        e.range = 'Minimum, default, and maximum must all be integers.';
      } else if (min >= max) {
        e.range = 'Minimum must be less than maximum.';
      } else if (def < min || def > max) {
        e.range = 'Default must be between minimum and maximum.';
      }

      return e;
    },
    isValid() {
      return Object.keys(this.errors).length === 0;
    },
    isDirty() {
      if (!this.initialSnapshot) return false;
      return JSON.stringify(this.serializeForm()) !== JSON.stringify(this.initialSnapshot);
    },
    // Preview props are clamped so the live preview never crashes on
    // half-typed values like an empty Number input (which v-model.number
    // surfaces as NaN). We don't show errors in the preview — those go
    // next to the form fields.
    safePreviewMin() {
      const n = Number(this.form.content.min);
      return Number.isFinite(n) ? n : 0;
    },
    safePreviewMax() {
      const n = Number(this.form.content.max);
      const min = this.safePreviewMin;
      return Number.isFinite(n) && n > min ? n : min + 1;
    },
    safePreviewDefault() {
      const n = Number(this.form.content.default);
      const min = this.safePreviewMin;
      const max = this.safePreviewMax;
      if (!Number.isFinite(n)) return min;
      if (n < min) return min;
      if (n > max) return max;
      return n;
    },
    previewSectionLabel() {
      const id = this.form.section_id;
      if (id === null || id === undefined) return '';
      const section = this.sections.find(s => s.id === id);
      if (!section) return '';
      // Show the section name in the active translation tab's language
      // so the preview matches what the user sees on that locale.
      return localizeI18n(section.name, this.activeLang);
    },
    // Drives the renderer's upper-right primer button. The button shows
    // when there's help text for the active locale; tapping it opens
    // the help text in a bottom sheet.
    primerContent() {
      const ht = this.form.help_text;
      return (ht && ht[this.activeLang]) ? ht[this.activeLang] : '';
    },
    primerLabel() {
      // Generic localized "Info" word. Keeps the button consistent
      // across questions — the body is the question-specific help text.
      const byLoc = { en: 'Info', de: 'Info', fr: 'Infos', it: 'Info' };
      return byLoc[this.activeLang] || 'Info';
    },
    // Locale-aware placeholders. DE matches the spec examples; other
    // locales get a translated hint where it's obvious, and a generic
    // dash where it isn't (kept short to avoid putting fake content
    // into the editor that might be mistaken for real translations).
    titlePlaceholder() {
      const byLoc = {
        de: 'Wie viele Portionen Gemüse oder Salat isst du pro Tag?',
        en: 'How many portions of vegetables or salad do you eat per day?',
        fr: '',
        it: '',
      };
      return byLoc[this.activeLang] || '';
    },
    promptPlaceholder() {
      const byLoc = {
        de: 'Tippe + oder − um deine Antwort anzupassen.',
        en: 'Tap + or − to adjust your answer.',
        fr: '',
        it: '',
      };
      return byLoc[this.activeLang] || '';
    },
    unitPlaceholder() {
      const byLoc = {
        de: 'Portionen',
        en: 'portions',
        fr: 'portions',
        it: 'porzioni',
      };
      return byLoc[this.activeLang] || '';
    },
    helpPlaceholder() {
      const byLoc = {
        de: 'Eine Portion entspricht etwa einer Handvoll oder 120 g.',
        en: 'One portion is about a handful, or 120 g.',
        fr: '',
        it: '',
      };
      return byLoc[this.activeLang] || '';
    },
  },
  watch: {
    '$route.params.id': {
      immediate: true,
      handler(id) {
        if (this.isEdit) {
          this.loadExisting(id);
        } else {
          this.resetForNew();
        }
      },
    },
  },
  created() {
    if (!this.isEdit) {
      this.resetForNew();
    }
  },
  mounted() {
    // Fetch sections if the cache is empty. Shared across StepperEditor
    // and (eventually) the bank page, so the second mount in a session
    // is a no-op.
    if (!questionsState.sections.length) {
      this.fetchSections();
    }
  },
  methods: {
    buildBlankForm() {
      return {
        code: '',
        section_id: null,
        status: 'active',
        content: blankStepperContent(),
        help_text: blankI18n(),
      };
    },

    // Format a section dropdown option. Section names are i18n; show the
    // user's UI-language version (English page chrome) with a fallback
    // chain. Append the questionnaire name when the same section name
    // exists in multiple questionnaires, so the user can disambiguate.
    sectionOptionLabel(section) {
      const name = localizeI18n(section.name, 'en');
      // Count sections with the same English-resolved name across the
      // current sections list. If more than one, append the questionnaire
      // context.
      const collisions = this.sections.filter(
        s => localizeI18n(s.name, 'en') === name,
      ).length;
      if (collisions > 1 && section.questionnaire_name) {
        return `${name} (${section.questionnaire_name})`;
      }
      return name || section.slug || `Section ${section.id}`;
    },

    async fetchSections() {
      try {
        const sections = await apiService.fetchSections();
        // Replace the reactive store contents in place so v-model and
        // computeds re-evaluate.
        questionsState.sections.splice(
          0,
          questionsState.sections.length,
          ...sections,
        );
      } catch (e) {
        // Non-fatal: the dropdown just stays empty ("Unassigned" still
        // selectable). Surface as a soft warning rather than blocking
        // the editor.
        console.warn('Could not load sections:', e);
      }
    },

    onSectionPicked(section) {
      this.form.section_id = section.id;
      const idx = questionsState.sections.findIndex(s => s.id === section.id);
      if (idx === -1) {
        questionsState.sections.push(section);
      } else {
        questionsState.sections.splice(idx, 1, section);
      }
      this.showSectionPicker = false;
    },

    // Returns true if a given locale has *any* content in any of the four
    // translatable fields. Drives the per-tab status dot.
    isLocaleFilled(loc) {
      const c = this.form.content;
      const fields = [
        c.title && c.title[loc],
        c.prompt && c.prompt[loc],
        c.unit_label && c.unit_label[loc],
        this.form.help_text && this.form.help_text[loc],
      ];
      return fields.some(v => typeof v === 'string' && v.trim().length > 0);
    },

    resetForNew() {
      this.form = this.buildBlankForm();
      this.initialSnapshot = this.serializeForm();
      this.answerCount = 0;
      this.loadError = null;
      this.serverError = null;
      this.touched = { code: false, title: false, unit_label: false };
    },

    async loadExisting(id) {
      // GET /api/seq/questions/:id via apiService. 404 yields a clear
      // "not found" state; other errors surface as a banner with retry
      // by router navigation.
      this.loadError = null;
      this.serverError = null;
      this.rejectedCodes = [];
      this.loading = true;
      try {
        const q = await apiService.fetchQuestion(id);
        this.form = {
          code: q.code,
          section_id: q.section_id,
          status: q.status,
          content: {
            title: ensureAllLocales(q.content?.title),
            prompt: ensureAllLocales(q.content?.prompt),
            unit_label: ensureAllLocales(q.content?.unit_label),
            min: q.content?.min ?? 0,
            max: q.content?.max ?? 10,
            default: q.content?.default ?? 2,
          },
          help_text: ensureAllLocales(q.help_text),
        };
        this.answerCount = q.answer_count || 0;
        this.initialSnapshot = this.serializeForm();
        this.touched = { code: false, title: false, unit_label: false };
      } catch (e) {
        this.initialSnapshot = null;
        if (e?.response?.status === 404) {
          this.loadError = `No question found with id ${id}.`;
        } else {
          const detail = e?.response?.data?.detail;
          this.loadError = typeof detail === 'string'
            ? detail
            : 'Failed to load question from the server.';
        }
      } finally {
        this.loading = false;
      }
    },

    // Snapshot of the form used for dirty-check and as the basis of
    // create/update payloads. Trims whitespace per-locale so trailing
    // spaces don't register as "dirty".
    serializeForm() {
      const f = this.form;
      return {
        code: (f.code || '').trim(),
        section_id: f.section_id ?? null,
        status: f.status,
        content: {
          title: trimI18n(f.content.title),
          prompt: trimI18n(f.content.prompt),
          unit_label: trimI18n(f.content.unit_label),
          min: f.content.min,
          max: f.content.max,
          default: f.content.default,
        },
        help_text: trimI18n(f.help_text),
      };
    },

    onCodeInput(e) {
      // Auto-uppercase. Saves the user a Shift key and keeps the code
      // aligned with the database CHECK constraint as they type.
      this.form.code = (e.target.value || '').toUpperCase();
    },

    async onSave() {
      // Force-touch fields so any pending errors show after a click on
      // an untouched form.
      this.touched.code = true;
      this.touched.title = true;
      this.touched.unit_label = true;
      if (!this.isValid || this.saving) return;

      this.serverError = null;
      this.saving = true;
      const payload = this.serializeForm();
      // serializeForm() already compacts each i18n object: empty locale
      // strings are dropped, so optional fields like prompt and help_text
      // arrive as {} when no language is filled.

      try {
        if (this.isEdit) {
          const updated = await apiService.updateQuestion(this.questionId, {
            code: payload.code,
            section_id: payload.section_id,
            status: payload.status,
            content: payload.content,
            help_text: payload.help_text,
          });
          // Update the local cache so a subsequent reload of the editor
          // (or a future bank page) reflects the change without refetch.
          this._upsertCached(updated);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', 'Question saved.');
          this.$router.push('/admin/questions');
        } else {
          const created = await apiService.createQuestion({
            code: payload.code,
            section_id: payload.section_id,
            question_type: 'numeric_stepper',
            content: payload.content,
            help_text: payload.help_text,
            status: payload.status,
          });
          this._upsertCached(created);
          this.initialSnapshot = this.serializeForm();
          this.showToast('success', `Created question ${created.code}.`);
          this.$router.push('/admin/questions');
        }
      } catch (e) {
        const status = e?.response?.status;
        const detail = e?.response?.data?.detail;
        if (status === 409) {
          // Duplicate code — keep the inline error glued to the field
          // until the user changes the code.
          const code = (this.form.code || '').trim();
          if (code && !this.rejectedCodes.includes(code)) {
            this.rejectedCodes.push(code);
          }
          this.serverError = typeof detail === 'string'
            ? detail
            : 'A question with that code already exists.';
        } else if (status === 422) {
          this.serverError = Array.isArray(detail)
            ? detail.map(d => d.msg).join(' ')
            : (typeof detail === 'string' ? detail : 'Validation failed.');
        } else if (status === 404) {
          this.serverError = 'The question no longer exists. It may have been deleted.';
        } else {
          this.serverError = typeof detail === 'string'
            ? detail
            : 'Could not save question. Please try again.';
        }
      } finally {
        this.saving = false;
      }
    },

    // Keeps questionsState.items in sync after a save so the local cache
    // mirrors the server. Mirrors the upsertQuestionnaire pattern from
    // _data/questionnaires.js.
    _upsertCached(item) {
      if (!item || item.id == null) return;
      const items = questionsState.items;
      const idx = items.findIndex(q => q.id === item.id);
      if (idx === -1) {
        items.push(item);
      } else {
        items.splice(idx, 1, item);
      }
    },

    onCancel() {
      if (this.isDirty) {
        this.cancelPending = true;
        this.showLeaveConfirm = true;
        return;
      }
      this.$router.push('/admin/questions');
    },

    onCancelLeave() {
      this.showLeaveConfirm = false;
      if (this.pendingNext) {
        this.pendingNext(false);
        this.pendingNext = null;
      }
      this.cancelPending = false;
    },

    onConfirmLeave() {
      this.showLeaveConfirm = false;
      if (this.cancelPending) {
        // User clicked Cancel button → just push manually.
        this.cancelPending = false;
        // Clear dirty state so beforeRouteLeave doesn't re-prompt.
        this.initialSnapshot = this.serializeForm();
        this.$router.push('/admin/questions');
        return;
      }
      if (this.pendingNext) {
        const next = this.pendingNext;
        this.pendingNext = null;
        // Clear dirty state so the guard doesn't re-fire if the user
        // navigates within the editor in unusual ways.
        this.initialSnapshot = this.serializeForm();
        next();
      }
    },

    showToast(kind, text) {
      if (this.toastTimer) {
        clearTimeout(this.toastTimer);
        this.toastTimer = null;
      }
      this.toast = { kind, text };
      const ms = kind === 'error' ? 6000 : 3500;
      this.toastTimer = setTimeout(() => {
        this.toast = null;
        this.toastTimer = null;
      }, ms);
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.isDirty && !this.cancelPending) {
      this.pendingNext = next;
      this.showLeaveConfirm = true;
    } else {
      next();
    }
  },
  beforeUnmount() {
    if (this.toastTimer) clearTimeout(this.toastTimer);
  },
};
</script>

<style scoped>
.stepper-editor-wrap {
  --color-bg-page: #ffffff;
  --color-bg-card: #ffffff;
  --color-bg-muted: #f5f4ef;
  --color-bg-soft: #fafaf7;
  --color-text-primary: #2c2c2a;
  --color-text-secondary: #5f5e5a;
  --color-text-tertiary: #888780;
  --color-border: rgba(0, 0, 0, 0.12);
  --color-border-strong: rgba(0, 0, 0, 0.2);
  --color-accent: #534AB7;
  --color-accent-soft: rgba(83, 74, 183, 0.12);
  --color-accent-dark: #3F368F;
  --color-bad: #C8392E;
  --color-bad-dark: #A92F26;
  --color-bad-soft: rgba(200, 57, 46, 0.10);
  --color-warn: #E8A33D;
  --color-warn-soft: rgba(232, 163, 61, 0.18);
  --color-warn-dark: #6B4A0F;
  --radius-md: 8px;
  --radius-lg: 12px;
  --font-sans: 'Montserrat', -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  --font-mono: "SF Mono", Menlo, Consolas, monospace;

  background: var(--color-bg-page);
  font-family: var(--font-sans);
  color: var(--color-text-primary);
  line-height: 1.6;
  min-height: 100%;
  box-sizing: border-box;
}

.stepper-editor-wrap *,
.stepper-editor-wrap *::before,
.stepper-editor-wrap *::after {
  box-sizing: border-box;
}

.page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 2.5rem 1.5rem 4rem;
}

.breadcrumb {
  font-size: 13px;
  color: var(--color-text-tertiary);
  margin-bottom: 1rem;
  letter-spacing: 0.02em;
}

.breadcrumb-link {
  color: var(--color-text-secondary);
  text-decoration: none;
  border-bottom: 1px solid transparent;
}

.breadcrumb-link:hover {
  border-bottom-color: var(--color-text-secondary);
}

.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
}

.page-header-text {
  flex: 1;
  min-width: 240px;
}

.page-header-actions {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
  margin-top: 4px;
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  margin: 0 0 0.5rem;
  line-height: 1.2;
}

.page-subtitle {
  font-size: 16px;
  color: var(--color-text-secondary);
  margin: 0;
  font-weight: 400;
}

.btn {
  padding: 9px 16px;
  border-radius: var(--radius-md);
  font-size: 13px;
  font-family: inherit;
  font-weight: 500;
  cursor: pointer;
  border: 0.5px solid var(--color-border);
  transition: opacity 0.15s, background 0.15s, border-color 0.15s, color 0.15s;
}

.btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}

.btn-secondary {
  background: var(--color-bg-card);
  color: var(--color-text-primary);
}

.btn-secondary:hover:not(:disabled) {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.btn-primary {
  background: var(--color-accent);
  color: white;
  border-color: var(--color-accent);
}

.btn-primary:hover:not(:disabled) {
  background: var(--color-accent-dark);
  border-color: var(--color-accent-dark);
}

.btn-danger {
  background: var(--color-bad);
  color: white;
  border-color: var(--color-bad);
}

.btn-danger:hover {
  background: var(--color-bad-dark);
  border-color: var(--color-bad-dark);
}

.server-error {
  font-size: 13px;
  color: #6B1F18;
  background: var(--color-bad-soft);
  border: 0.5px solid rgba(200, 57, 46, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.answer-warning {
  font-size: 13px;
  color: var(--color-warn-dark);
  background: var(--color-warn-soft);
  border: 0.5px solid rgba(232, 163, 61, 0.45);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  margin: 0 0 1.5rem;
  line-height: 1.5;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}

.warning-icon {
  font-size: 14px;
  line-height: 1.4;
}

.load-error {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-secondary);
}

.loading-state {
  text-align: center;
  padding: 3rem 1rem;
  color: var(--color-text-tertiary);
  font-size: 14px;
}

.back-link {
  color: var(--color-accent);
  text-decoration: none;
  font-size: 14px;
}

.back-link:hover {
  text-decoration: underline;
}

/* ───────── Two-column grid ───────── */
.editor-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 360px;
  gap: 32px;
  align-items: start;
}

@media (max-width: 900px) {
  .editor-grid {
    grid-template-columns: 1fr;
  }
}

.form-column {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  min-width: 0;
}

.preview-column {
  position: sticky;
  top: 1.5rem;
}

.preview-label {
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  font-weight: 600;
  margin-bottom: 8px;
}

.preview-frame {
  /* The renderer brings its own beige page background + white screen
     card, mirroring VanillaD. The frame here is just a thin outer
     boundary so the preview reads as a contained mock screen rather
     than blending into the editor surface. */
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.04);
}

.preview-note {
  margin: 10px 0 0;
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  font-style: italic;
  line-height: 1.4;
}

/* ───────── Fieldsets / fields ───────── */
.fieldset {
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-lg);
  padding: 1.25rem 1.5rem 1.5rem;
  margin: 0;
  background: var(--color-bg-card);
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.fieldset-legend {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-tertiary);
  padding: 0 6px;
  margin-left: -6px;
}

/* Range row sits inside the same fieldset as the i18n fields but is
   visually separated by a thin divider so it reads as language-neutral. */
.range-field {
  border-top: 0.5px dashed var(--color-border);
  padding-top: 1rem;
  margin-top: 0.25rem;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.field-label {
  font-size: 13px;
  font-weight: 500;
  color: var(--color-text-secondary);
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  flex-wrap: wrap;
}

.lang-tag {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--color-accent);
  background: var(--color-accent-soft);
  padding: 1px 6px;
  border-radius: 3px;
}

.optional {
  color: var(--color-text-tertiary);
  font-weight: 400;
  font-size: 12px;
}

.field-input {
  width: 100%;
  padding: 10px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-primary);
  font-family: var(--font-sans);
  font-size: 14px;
  line-height: 1.4;
}

.field-input.mono {
  font-family: var(--font-mono);
  font-size: 13px;
  letter-spacing: 0.02em;
}

.field-input:focus {
  outline: none;
  border-color: var(--color-accent);
  box-shadow: 0 0 0 2px var(--color-accent-soft);
}

.field-input.has-error {
  border-color: var(--color-bad);
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.15);
}

.field-input.has-error:focus {
  box-shadow: 0 0 0 2px rgba(200, 57, 46, 0.25);
}

.field-textarea {
  resize: vertical;
  min-height: 60px;
  font-family: var(--font-sans);
}

.field-select {
  appearance: auto;
}

/* Section picker button (same as SingleChoice/Matrix editors). */
.section-pick-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 9px 12px;
  border: 0.5px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  font-family: inherit;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}

.section-pick-btn:hover {
  background: var(--color-bg-soft);
  border-color: var(--color-border-strong);
}

.section-pick-btn.is-unassigned .section-pick-label {
  color: var(--color-text-tertiary);
  font-style: italic;
}

.section-pick-action {
  font-size: 12px;
  font-weight: 600;
  color: var(--color-accent);
  letter-spacing: 0.02em;
  flex-shrink: 0;
}

.section-clear-btn {
  align-self: flex-start;
  margin-top: 4px;
  background: none;
  border: none;
  color: var(--color-text-tertiary);
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  padding: 2px 0;
  text-decoration: underline;
}

.section-clear-btn:hover {
  color: var(--color-text-secondary);
}

.field-hint {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  margin: 0;
  line-height: 1.4;
}

.field-hint.dim {
  font-style: italic;
  color: var(--color-warn-dark);
}

.field-error {
  font-size: 12px;
  color: var(--color-bad);
  margin: 0;
}

.radio-row {
  display: flex;
  gap: 18px;
  padding: 4px 0;
}

.radio-option {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: var(--color-text-primary);
  cursor: pointer;
}

.radio-option input {
  accent-color: var(--color-accent);
  cursor: pointer;
}

.range-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.range-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.range-sublabel {
  font-size: 11.5px;
  color: var(--color-text-tertiary);
  font-weight: 500;
}

.number-input {
  font-family: var(--font-mono);
  font-size: 14px;
  text-align: center;
}

/* ───────── Modal + toast (mirrors AdminQuestionnaireDetail) ───────── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  z-index: 2000;
}

.modal {
  background: var(--color-bg-card);
  border-radius: var(--radius-lg);
  border: 0.5px solid var(--color-border);
  padding: 1.5rem;
  max-width: 420px;
  width: 100%;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px;
}

.modal-text {
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.toast {
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  background: var(--color-text-primary);
  color: white;
  padding: 11px 18px;
  border-radius: var(--radius-md);
  font-size: 13px;
  line-height: 1.4;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
  max-width: min(90vw, 520px);
  z-index: 3000;
}

.toast-success {
  background: #2E7D32;
}

.toast-error {
  background: var(--color-bad-dark);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 8px);
}
</style>
